import { Transition } from '@headlessui/react';
import { Fragment, useState } from 'react';
import { classNames } from '@utils/classNames';
import { ButtonType } from '@src/types/types';
import { Button } from '@components/atoms/Button';
import { Icon } from '@components/atoms/Icon';
import { IconType } from '@src/types/IconType';
import { ToastProp, ToastType } from '@components/toasts/toast.types';

const toastStyles: Record<
  ToastType,
  { borderClass: string; iconType: IconType; iconColor: string }
> = {
  [ToastType.SUCCESS]: {
    borderClass: 'border-solid border-2 border-green',
    iconType: IconType.CheckmarkBubble,
    iconColor: 'text-green',
  },
  [ToastType.INFO]: {
    borderClass: 'border-solid border-2 border-ivarioBlue',
    iconType: IconType.Information,
    iconColor: 'text-grey2',
  },
  [ToastType.ERROR]: {
    borderClass: 'border-solid border-2 border-error',
    iconType: IconType.Cross,
    iconColor: 'text-error',
  },
} as const;

export function Toast({ onClose, toastType, title, subTitle }: ToastProp) {
  const [open, setOpen] = useState(true);
  return (
    <>
      <Transition
        appear
        show={open}
        as={Fragment}
        enter="transform ease-out duration-300 transition"
        enterFrom="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
        enterTo="translate-y-0 opacity-100 sm:translate-x-0"
        leave="transition ease-in duration-100"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
        afterLeave={onClose}
      >
        <div
          className={classNames(
            'pointer-events-auto w-[496px] h-16 max-w-full overflow-hidden rounded-bonusLarge bg-white shadow-lg py-3 pr-3 relative',
            toastStyles[toastType].borderClass,
          )}
        >
          <div
            className={classNames(
              'rounded-bonusLarge absolute top-3.5 bottom-3.5 w-[3px]',
            )}
          />
          <div className="flex items-center">
            <div className="ml-4 flex flex-shrink-0">
              <Icon
                type={toastStyles[toastType].iconType}
                className={classNames(
                  'h-8 w-8',
                  toastStyles[toastType].iconColor,
                )}
                aria-hidden="true"
              />
            </div>
            <div className="flex w-0 flex-1 justify-between">
              <div className="ml-4 w-0 flex-1">
                <p className="text-sm font-bold text-dark">{title}</p>
                <p className="mt-0.5 text-xs font-light text-dark">
                  {subTitle}
                </p>
              </div>
            </div>
            <div className="ml-4 flex flex-shrink-0">
              <Button
                buttonType={ButtonType.CLOSE}
                onClick={() => setOpen(false)}
                tabIndex={0}
                data-testid="close-toast-btn"
                IconTrailing={IconType.Cross}
                size="small"
              />
            </div>
          </div>
        </div>
      </Transition>
    </>
  );
}
